// KÖZÖS árazó-blokk (pricingType-függő) — a szolgáltatás-KATALÓGUS plan-editorja ÉS a szolgáltatás-HOZZÁRENDELÉS
// ár-override blokkja is ezt használja (DRY). A mátrix: flat=egységár; commission=minimum; tiered=passzív díj +
// sávok + overflow; base_tiered=alapdíj + sávok + overflow. A mezők a KÖZÖS form-fields-re épülnek, egy `base`
// mező-út-prefix alatt (`plans.0` a katalógusnál, `pricing` a hozzárendelésnél) — a szülő `<Form {...form}>`
// FormProvider-e alatt.
//
// i18n: a hívó adja a `t`-t + a `tPrefix`-et (mindkét modul azonos kulcs-struktúrával: `<prefix>.section`,
// `<prefix>.primaryLabel.<pricingType>`, `<prefix>.tiers`, …). A `disabled` a teljes blokkot zárolja (szerkesztés-
// lock: számlázott hozzárendelésnél az ár nem módosítható).
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';
import {
  type FieldArrayPath,
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
  useFieldArray,
} from 'react-hook-form';

import { SelectField, TextField } from '@/components/shared/form-fields';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CURRENCIES,
  type Currency,
  emptyTier,
  hasTiers,
  type PricingType,
} from '@/lib/pricing-form';

interface PricingEditorProps<TValues extends FieldValues> {
  form: UseFormReturn<TValues>;
  /** A pricing-objektum mező-út-prefixe (pl. `plans.0` vagy `pricing`). */
  base: string;
  /** Az aktuális pricingType (a mátrix-ág vezérlője) — a hívó watch-olja. */
  pricingType: PricingType;
  /** i18n-fordító + kulcs-prefix (`serviceCatalog.pricing` / `serviceAssignments.pricing`). */
  t: TFunction;
  tPrefix: string;
  /** Teljes blokk-zárolás (szerkesztés-lock). */
  disabled?: boolean;
}

export function PricingEditor<TValues extends FieldValues>({
  form,
  base,
  pricingType,
  t,
  tPrefix,
  disabled = false,
}: PricingEditorProps<TValues>): JSX.Element {
  const { control, watch, setValue, getFieldState } = form;

  // A dinamikus, futásidejű mező-utak: a react-hook-form típusrendszere a template-literal utakat generikus
  // TValues mellett nem tudja kikövetkeztetni → EGY, dokumentált határ-cast (a leaf-nevek fixek, biztonságos).
  const path = (leaf: string): FieldPath<TValues> => `${base}.${leaf}` as FieldPath<TValues>;
  const tiersPath = `${base}.tiers` as FieldArrayPath<TValues>;

  const rawCurrency = watch(path('primaryCurrency'));
  const primaryCurrency: Currency = typeof rawCurrency === 'string' ? rawCurrency : 'HUF';
  const overflowEnabled = Boolean(watch(path('overflowEnabled')));

  const tiersArray = useFieldArray({ control, name: tiersPath });

  const currencyOptions = CURRENCIES.map((v) => ({ value: v, label: v }));
  // Az elsődleges ár címkéje a pricingType-ból ÁLL ÖSSZE, ezért — a modul minden más dinamikus kulcsához
  // hasonlóan — tartalékot kap: hiányzó/ismeretlen (üres) típusnál az i18next a KULCSOT adná vissza, és a
  // felületen nyers `<prefix>.primaryLabel.` jelent meg címke helyett. A tartalék a típus-független nettó-ár
  // címke (sosem állít valótlant az ár-sorról).
  const primaryLabel = t(`${tPrefix}.primaryLabel.${pricingType}`, {
    defaultValue: t(`${tPrefix}.netPrice`),
  });
  const tiersError = getFieldState(path('tiers')).error?.message;

  return (
    <div className="border-border space-y-4 rounded-lg border p-4">
      <p className="text-sm font-semibold">{t(`${tPrefix}.section`)}</p>

      {/* Elsődleges ár (flat=egységár; commission=minimum; tiered=passzív; base_tiered=alapdíj). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <TextField<TValues>
            name={path('primaryPrice')}
            label={primaryLabel}
            required
            placeholder="0"
            autoComplete="off"
            disabled={disabled}
          />
        </div>
        <SelectField<TValues>
          name={path('primaryCurrency')}
          label={t(`${tPrefix}.currency`)}
          required
          options={currencyOptions}
          disabled={disabled}
        />
      </div>

      {/* Sávok (tiered / base_tiered). */}
      {hasTiers(pricingType) && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{t(`${tPrefix}.tiers`)}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => tiersArray.append(emptyTier(primaryCurrency) as never)}
            >
              <PlusIcon className="h-4 w-4" />
              {t(`${tPrefix}.addTier`)}
            </Button>
          </div>
          {tiersError && <p className="text-destructive text-sm">{tiersError}</p>}
          {tiersArray.fields.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t(`${tPrefix}.noTiers`)}</p>
          ) : (
            <div className="space-y-3">
              {tiersArray.fields.map((tierField, tierIndex) => (
                <div
                  key={tierField.id}
                  className="border-border relative grid grid-cols-2 gap-3 rounded-lg border p-3 sm:grid-cols-4"
                >
                  <TextField<TValues>
                    name={path(`tiers.${tierIndex}.tierMin`)}
                    label={t(`${tPrefix}.tierMin`)}
                    required
                    autoComplete="off"
                    disabled={disabled}
                  />
                  <TextField<TValues>
                    name={path(`tiers.${tierIndex}.tierMax`)}
                    label={t(`${tPrefix}.tierMax`)}
                    placeholder={t(`${tPrefix}.tierMaxUnlimited`)}
                    autoComplete="off"
                    disabled={disabled}
                  />
                  <TextField<TValues>
                    name={path(`tiers.${tierIndex}.netPrice`)}
                    label={t(`${tPrefix}.netPrice`)}
                    required
                    autoComplete="off"
                    disabled={disabled}
                  />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <SelectField<TValues>
                        name={path(`tiers.${tierIndex}.currency`)}
                        label={t(`${tPrefix}.currency`)}
                        options={currencyOptions}
                        disabled={disabled}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={disabled}
                      aria-label={t(`${tPrefix}.removeTier`)}
                      onClick={() => tiersArray.remove(tierIndex)}
                    >
                      <TrashIcon className="text-destructive h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Overflow (sáv-feletti) — opcionális blokk. */}
          {/* A globális (Radix) Checkbox — a natív `<input type="checkbox">` helyett: egységes fókusz-állapot
              és a11y. A `label` `htmlFor`-ral kötve (a Radix gomb-elem, tehát a beburkolás nem elég). */}
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id={`${tPrefix}-overflowEnabled`}
              checked={overflowEnabled}
              disabled={disabled}
              onCheckedChange={(checked) =>
                setValue(path('overflowEnabled'), (checked === true) as never, {
                  shouldDirty: true,
                })
              }
            />
            <label htmlFor={`${tPrefix}-overflowEnabled`}>{t(`${tPrefix}.overflowEnabled`)}</label>
          </div>
          {overflowEnabled && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextField<TValues>
                name={path('overflowBlockSize')}
                label={t(`${tPrefix}.blockSize`)}
                required
                autoComplete="off"
                disabled={disabled}
              />
              <TextField<TValues>
                name={path('overflowPrice')}
                label={t(`${tPrefix}.netPrice`)}
                required
                autoComplete="off"
                disabled={disabled}
              />
              <SelectField<TValues>
                name={path('overflowCurrency')}
                label={t(`${tPrefix}.currency`)}
                options={currencyOptions}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
